import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Edit, Eye, ChevronUp, ChevronDown, Trash2, DollarSign } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR, todayLocalISO, formatIdade, idadeDetalhada } from '../utils/formatDate';
import toast from 'react-hot-toast';

const SITUACAO_BADGE = { ativo:'badge-green', vendido:'badge-blue', descarte:'badge-red', obito:'badge-gray', quarentena:'badge-yellow' };
const CATEGORIA_BADGE = { cordeiro:'badge-blue', borrego:'badge-green', matriz:'badge-orange', reprodutor:'badge-orange', descarte:'badge-red' };

const CATEGORIAS = ['cordeiro','borrego','reprodutor','matriz'];
const RACAS_FIXAS = ['Dorper','White Dorper','Santa Inês','SRD'];

const TIMELINE_CORES = {
  pesagem:     { cor: '#2563EB', bg: '#DBEAFE', icon: '⚖️' },
  intervencao: { cor: '#DC2626', bg: '#FEE2E2', icon: '💉' },
  famacha:     { cor: '#EA580C', bg: '#FFEDD5', icon: '🔴' },
  cobertura:   { cor: '#7C3AED', bg: '#EDE9FE', icon: '💜' },
  parto:       { cor: '#EC4899', bg: '#FCE7F3', icon: '👶' },
  amamentacao: { cor: '#0891B2', bg: '#CFFAFE', icon: '🍼' },
  escore:      { cor: '#6B7280', bg: '#F3F4F6', icon: '📊' },
};

// Categoria por idade: <6 meses -> cordeiro, 6-11 meses -> borrego, >=12 meses -> reprodutor/matriz.
function calcularCategoria(sexo, dataNascimento) {
  if (!sexo || !dataNascimento) return '';
  const d = idadeDetalhada(dataNascimento);
  if (!d) return '';
  const totalMeses = d.anos * 12 + d.meses;
  if (totalMeses >= 12) return sexo === 'M' ? 'reprodutor' : 'matriz';
  if (totalMeses >= 6) return 'borrego';
  return 'cordeiro';
}

export default function Animais() {
  const [animais, setAnimais] = useState([]); const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true); const [busca, setBusca] = useState('');
  const [filtros, setFiltros] = useState({ situacao:'cabana', categoria:'', sexo:'', lote_id:'' });
  const [sort, setSort] = useState({ col: 'brinco', dir: 'asc' });
  const [showModal, setShowModal] = useState(false); const [editando, setEditando] = useState(null);
  const [racas, setRacas] = useState([]); const [lotes, setLotes] = useState([]);
  const [form, setForm] = useState({ brinco:'',nome:'',sexo:'M',categoria:'',situacao:'ativo',origem:'nascido',data_nascimento:'',raca_id:'',lote_id:'',data_entrada:'',valor_compra:'',observacoes:'' });

  const [animalDetalhe, setAnimalDetalhe] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [custosDetalhe, setCustosDetalhe] = useState(null);
  const [showDetalhe, setShowDetalhe] = useState(false);
  const [showCustoModal, setShowCustoModal] = useState(false);
  const [detalheLoading, setDetalheLoading] = useState(false);
  const hoje = todayLocalISO();
  const [formCusto, setFormCusto] = useState({ tipo:'outro', descricao:'', valor:'', data:hoje });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filtros, busca, limit: 500 };
      const { data } = await api.get('/animais', { params });
      setAnimais(data.data); setTotal(data.total);
    } finally { setLoading(false); }
  }, [filtros, busca]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/racas').then(r => setRacas(r.data));
    api.get('/lotes').then(r => setLotes(r.data));
  }, []);

  // Ordenação client-side
  const sorted = [...animais].sort((a, b) => {
    let va = a[sort.col] ?? ''; let vb = b[sort.col] ?? '';
    if (sort.col === 'idade_dias') { va = a.idade_dias || 0; vb = b.idade_dias || 0; }
    if (typeof va === 'number') return sort.dir === 'asc' ? va - vb : vb - va;
    return sort.dir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
  });

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ col }) => sort.col === col ? (sort.dir === 'asc' ? <ChevronUp size={13}/> : <ChevronDown size={13}/>) : <ChevronDown size={13} style={{ opacity: 0.3 }}/>;

  const openModal = (animal = null) => {
    setEditando(animal);
    if (animal) {
      setForm({ brinco:animal.brinco,nome:animal.nome||'',sexo:animal.sexo,categoria:animal.categoria,situacao:animal.situacao,origem:animal.origem,data_nascimento:animal.data_nascimento?.split('T')[0]||'',raca_id:animal.raca_id||'',lote_id:animal.lote_id||'',data_entrada:animal.data_entrada?.split('T')[0]||'',valor_compra:animal.valor_compra ?? '',observacoes:animal.observacoes||'' });
    } else {
      setForm({ brinco:'',nome:'',sexo:'M',categoria:'',situacao:'ativo',origem:'nascido',data_nascimento:'',raca_id:'',lote_id:'',data_entrada:'',valor_compra:'',observacoes:'' });
    }
    setShowModal(true);
  };

  const abrirDetalhe = async (animalId) => {
    setDetalheLoading(true); setShowDetalhe(true);
    try {
      const [detRes, tlRes, custosRes] = await Promise.all([
        api.get(`/animais/${animalId}`),
        api.get(`/animais/${animalId}/timeline`),
        api.get(`/animais/${animalId}/custos`),
      ]);
      setAnimalDetalhe(detRes.data); setTimeline(tlRes.data); setCustosDetalhe(custosRes.data);
    } catch { toast.error('Erro ao carregar detalhes'); }
    finally { setDetalheLoading(false); }
  };

  const fecharDetalhe = () => { setShowDetalhe(false); setAnimalDetalhe(null); setTimeline([]); setCustosDetalhe(null); };

  const carregarCustosDetalhe = async () => {
    if (!animalDetalhe?.id) return;
    const { data } = await api.get(`/animais/${animalDetalhe.id}/custos`);
    setCustosDetalhe(data);
  };

  const salvarCustoManual = async () => {
    if (!animalDetalhe?.id) return;
    if (!formCusto.descricao || !formCusto.valor) return toast.error('Preencha descrição e valor');
    try {
      await api.post(`/animais/${animalDetalhe.id}/custos`, { ...formCusto, valor: parseFloat(formCusto.valor) });
      toast.success('Custo registrado');
      setShowCustoModal(false);
      setFormCusto({ tipo:'outro', descricao:'', valor:'', data:hoje });
      carregarCustosDetalhe();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao registrar custo'); }
  };

  const excluirAnimal = async (id, brinco) => {
    if (!window.confirm(`Excluir "${brinco}" permanentemente? Todo o histórico será removido.`)) return;
    try { await api.delete(`/animais/${id}`); toast.success(`Animal ${brinco} excluído`); load(); if (showDetalhe) fecharDetalhe(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro ao excluir'); }
  };

  const salvar = async () => {
    try {
      const payload = { ...form };
      // Auto-categoria se não escolhida
      if (!payload.categoria && payload.data_nascimento && payload.sexo) {
        payload.categoria = calcularCategoria(payload.sexo, payload.data_nascimento);
      }
      if (editando) await api.put(`/animais/${editando.id}`, payload);
      else await api.post('/animais', payload);
      toast.success(editando ? 'Animal atualizado!' : 'Animal cadastrado!');
      setShowModal(false); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao salvar'); }
  };

  const f = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      // Auto-calcular categoria ao mudar sexo ou data_nascimento
      if ((k === 'sexo' || k === 'data_nascimento') && next.data_nascimento && next.sexo) {
        next.categoria = calcularCategoria(next.sexo, next.data_nascimento);
      }
      return next;
    });
  };

  // Raças para o select: apenas as 4 permitidas
  const racasDisponiveis = racas.filter(r => RACAS_FIXAS.includes(r.nome));

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h1>Rebanho — Animais</h1><p>{total} animais encontrados</p></div>
        <button className="btn btn-primary" onClick={() => openModal()}><Plus size={16} /> Novo Animal</button>
      </div>

      <div className="card" style={{ marginBottom:'16px' }}>
        <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
          <div className="search-box" style={{ flex:1, minWidth:'200px' }}>
            <Search size={16} />
            <input placeholder="Buscar por brinco, nome ou chip..." value={busca} onChange={e => setBusca(e.target.value)} />
          </div>
          <select value={filtros.situacao} onChange={e => setFiltros(p => ({ ...p, situacao: e.target.value }))} style={{ width:'auto' }}>
            <option value="cabana">Na Cabaña</option>
            <option value="">Todas situações</option>
            <option value="ativo">Ativo</option><option value="vendido">Vendido</option>
            <option value="descarte">Descarte</option><option value="obito">Óbito</option><option value="quarentena">Quarentena</option>
          </select>
          <select value={filtros.categoria} onChange={e => setFiltros(p => ({ ...p, categoria: e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todas categorias</option>
            {CATEGORIAS.map(c => <option key={c} value={c} style={{ textTransform:'capitalize' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
          <select value={filtros.sexo} onChange={e => setFiltros(p => ({ ...p, sexo: e.target.value }))} style={{ width:'auto' }}>
            <option value="">M / F</option><option value="M">Macho</option><option value="F">Fêmea</option>
          </select>
          <select value={filtros.lote_id} onChange={e => setFiltros(p => ({ ...p, lote_id: e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos os lotes</option>
            {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr>
            {[
              { col:'brinco', label:'Brinco' }, { col:'nome', label:'Nome' },
              { col:'sexo', label:'Sexo' }, { col:'raca_nome', label:'Raça' },
              { col:'categoria', label:'Categoria' }, { col:'lote_nome', label:'Lote' },
              { col:'situacao', label:'Situação' }, { col:'idade_dias', label:'Idade' },
            ].map(({ col, label }) => (
              <th key={col} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }} onClick={() => toggleSort(col)}>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4 }}>{label} <SortIcon col={col} /></span>
              </th>
            ))}
            <th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} style={{ textAlign:'center', padding:'40px', color:'var(--text-secondary)' }}>Carregando...</td></tr>
            : sorted.length === 0 ? <tr><td colSpan={9}><div className="empty-state"><p>Nenhum animal encontrado</p></div></td></tr>
            : sorted.map(a => (
              <tr key={a.id}>
                <td><strong style={{ color:'var(--color-primary)', cursor:'pointer', textDecoration:'underline' }} onClick={() => abrirDetalhe(a.id)}>{a.brinco}</strong></td>
                <td><span style={{ cursor:'pointer', color:'var(--color-primary)' }} onClick={() => abrirDetalhe(a.id)}>{a.nome || '—'}</span></td>
                <td><span className={`badge ${a.sexo==='M'?'badge-blue':'badge-orange'}`}>{a.sexo==='M'?'Macho':'Fêmea'}</span></td>
                <td>{a.raca_nome || '—'}</td>
                <td><span className={`badge ${CATEGORIA_BADGE[a.categoria]||'badge-gray'}`} style={{ textTransform:'capitalize' }}>{a.categoria}</span></td>
                <td>{a.lote_nome || '—'}</td>
                <td><span className={`badge ${SITUACAO_BADGE[a.situacao]||'badge-gray'}`}>{a.situacao}</span></td>
                <td style={{ color:'var(--text-secondary)', fontSize:'13px' }}>{formatIdade(a.data_nascimento, a.data_obito)}</td>
                <td style={{ whiteSpace:'nowrap' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openModal(a)}><Edit size={13}/></button>
                    <button className="btn btn-secondary btn-sm" onClick={() => excluirAnimal(a.id, a.brinco)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ═══ MODAL DETALHE + TIMELINE ═══ */}
      {showDetalhe && (
        <div className="modal-overlay" onClick={fecharDetalhe}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:'800px', maxHeight:'90vh', overflow:'auto', width:'95%' }}>
            <div className="modal-header" style={{ position:'sticky', top:0, background:'var(--bg-card)', zIndex:10 }}>
              <h2 style={{ display:'flex', alignItems:'center', gap:8 }}><Eye size={18} /> Ficha do Animal</h2>
              <div style={{ display:'flex', gap:8 }}>
                {animalDetalhe && <button className="btn btn-secondary btn-sm" style={{ color:'#DC2626' }} onClick={() => excluirAnimal(animalDetalhe.id, animalDetalhe.brinco)}><Trash2 size={13}/></button>}
                <button className="btn btn-secondary btn-sm" onClick={fecharDetalhe}>✕</button>
              </div>
            </div>
            {detalheLoading ? <div style={{ textAlign:'center', padding:60, color:'var(--text-secondary)' }}>Carregando...</div>
            : animalDetalhe ? (
              <div className="modal-body" style={{ padding:'0 20px 20px' }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20, padding:16, background:'var(--bg-main)', borderRadius:10 }}>
                  <div><div style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1 }}>Brinco</div><div style={{ fontSize:22, fontWeight:700, color:'var(--color-primary)' }}>{animalDetalhe.brinco}</div></div>
                  <div><div style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1 }}>Nome</div><div style={{ fontSize:18, fontWeight:600 }}>{animalDetalhe.nome || '—'}</div></div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginBottom:20 }}>
                  {[
                    { label:'Sexo', value: animalDetalhe.sexo === 'M' ? 'Macho' : 'Fêmea' },
                    { label:'Raça', value: animalDetalhe.raca_nome || '—' },
                    { label:'Categoria', value: animalDetalhe.categoria || '—' },
                    { label:'Situação', value: animalDetalhe.situacao || '—' },
                    { label:'Idade', value: formatIdade(animalDetalhe.data_nascimento, animalDetalhe.data_obito) },
                    { label:'Lote', value: animalDetalhe.lote_nome || '—' },
                    { label:'Nascimento', value: animalDetalhe.data_nascimento ? formatDateBR(animalDetalhe.data_nascimento) : '—' },
                    { label:'Origem', value: animalDetalhe.origem || '—' },
                    ...(animalDetalhe.origem === 'comprado' ? [{ label:'Valor de Aquisição', value: animalDetalhe.valor_compra != null ? `R$ ${parseFloat(animalDetalhe.valor_compra).toFixed(2)}` : '—' }] : []),
                  ].map((item, i) => (
                    <div key={i} style={{ padding:'10px 12px', background:'var(--bg-main)', borderRadius:8 }}>
                      <div style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase' }}>{item.label}</div>
                      <div style={{ fontSize:14, fontWeight:600, marginTop:2, textTransform:'capitalize' }}>{item.value}</div>
                    </div>
                  ))}
                </div>
                {(animalDetalhe.pai_brinco || animalDetalhe.mae_brinco) && (
                  <div style={{ display:'flex', gap:16, marginBottom:20 }}>
                    {animalDetalhe.pai_brinco && <div style={{ flex:1, padding:10, background:'#DBEAFE', borderRadius:8, fontSize:13 }}><strong>Pai:</strong> {animalDetalhe.pai_brinco}{animalDetalhe.pai_nome ? ' — ' + animalDetalhe.pai_nome : ''}</div>}
                    {animalDetalhe.mae_brinco && <div style={{ flex:1, padding:10, background:'#FCE7F3', borderRadius:8, fontSize:13 }}><strong>Mãe:</strong> {animalDetalhe.mae_brinco}{animalDetalhe.mae_nome ? ' — ' + animalDetalhe.mae_nome : ''}</div>}
                  </div>
                )}
                <div style={{ marginBottom:20, padding:12, background:'#F8FAFC', border:'1px solid var(--border-color)', borderRadius:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <div style={{ fontSize:14, fontWeight:700, display:'flex', alignItems:'center', gap:6 }}><DollarSign size={15} /> Custos do Animal</div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowCustoModal(true)}><Plus size={13} /> Lançar Custo</button>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:8, marginBottom:10 }}>
                    {[
                      ['Total', custosDetalhe?.totais?.total],
                      ['Compra', custosDetalhe?.totais?.compra],
                      ['Alimentação', custosDetalhe?.totais?.alimentacao],
                      ['Medicamentos', custosDetalhe?.totais?.medicamento],
                      ['Sanitário', custosDetalhe?.totais?.sanitario],
                      ['Outros', custosDetalhe?.totais?.outro],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background:'white', border:'1px solid var(--border-color)', borderRadius:8, padding:8 }}>
                        <div style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase' }}>{label}</div>
                        <div style={{ fontSize:14, fontWeight:700 }}>R$ {parseFloat(value || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                  {custosDetalhe?.lancamentos?.length ? (
                    <table style={{ fontSize:12 }}>
                      <thead><tr><th>Data</th><th>Tipo</th><th>Descrição</th><th>Origem</th><th style={{ textAlign:'right' }}>Valor</th></tr></thead>
                      <tbody>{custosDetalhe.lancamentos.slice(0, 6).map(c => (
                        <tr key={c.id}>
                          <td>{formatDateBR(c.data)}</td>
                          <td style={{ textTransform:'capitalize' }}>{c.tipo}</td>
                          <td>{c.descricao}</td>
                          <td>{c.referencia_tipo || 'manual'}</td>
                          <td style={{ textAlign:'right', fontWeight:600 }}>R$ {parseFloat(c.valor).toFixed(2)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  ) : <div style={{ color:'var(--text-secondary)', fontSize:13 }}>Nenhum custo registrado.</div>}
                </div>
                {animalDetalhe.crias?.length > 0 && (
                  <div style={{ marginBottom:20, padding:12, background:'#F0FDF4', borderRadius:8 }}>
                    <div style={{ fontSize:13, fontWeight:700, marginBottom:6 }}>🐑 Crias ({animalDetalhe.crias.length})</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {animalDetalhe.crias.map(c => (
                        <span key={c.id} className="badge badge-green" style={{ cursor:'pointer' }} onClick={() => { fecharDetalhe(); setTimeout(() => abrirDetalhe(c.id), 200); }}>
                          {c.brinco}{c.nome ? ' — ' + c.nome : ''} ({c.sexo})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ borderTop:'2px solid var(--border-color)', paddingTop:16, marginTop:8 }}>
                  <h3 style={{ fontSize:16, fontWeight:700, marginBottom:16 }}>Histórico ({timeline.length} eventos)</h3>
                  {timeline.length === 0 ? <div style={{ textAlign:'center', padding:30, color:'var(--text-secondary)' }}>Nenhum evento registrado.</div>
                  : (
                    <div style={{ position:'relative', paddingLeft:28 }}>
                      <div style={{ position:'absolute', left:11, top:0, bottom:0, width:2, background:'var(--border-color)' }} />
                      {timeline.map((ev, i) => {
                        const info = TIMELINE_CORES[ev.tipo] || TIMELINE_CORES.escore;
                        return (
                          <div key={i} style={{ position:'relative', marginBottom:16 }}>
                            <div style={{ position:'absolute', left:-22, top:4, width:22, height:22, borderRadius:'50%', background:info.bg, border:`2px solid ${info.cor}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, zIndex:1 }}>{info.icon}</div>
                            <div style={{ marginLeft:10, padding:'10px 14px', background:'var(--bg-main)', borderRadius:8, borderLeft:`3px solid ${info.cor}` }}>
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                                <div style={{ fontSize:14, fontWeight:600 }}>{ev.titulo}</div>
                                <div style={{ fontSize:11, color:'var(--text-secondary)', whiteSpace:'nowrap', marginLeft:12 }}>{ev.data ? formatDateBR(ev.data) : '—'}</div>
                              </div>
                              {ev.detalhe && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>{ev.detalhe}</div>}
                              {ev.obs && <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3, fontStyle:'italic' }}>{ev.obs}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : <div style={{ textAlign:'center', padding:40, color:'var(--text-secondary)' }}>Animal não encontrado</div>}
          </div>
        </div>
      )}

      {showCustoModal && animalDetalhe && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCustoModal(false)}>
          <div className="modal" style={{ maxWidth:420 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Lançar Custo</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCustoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label>Tipo</label>
                <select value={formCusto.tipo} onChange={e => setFormCusto(p => ({ ...p, tipo:e.target.value }))}>
                  <option value="compra">Compra</option>
                  <option value="alimentacao">Alimentação</option>
                  <option value="medicamento">Medicamento</option>
                  <option value="sanitario">Sanitário</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
              <div className="form-group"><label>Descrição *</label><input value={formCusto.descricao} onChange={e => setFormCusto(p => ({ ...p, descricao:e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label>Valor (R$) *</label><input type="number" min="0.01" step="0.01" value={formCusto.valor} onChange={e => setFormCusto(p => ({ ...p, valor:e.target.value }))} /></div>
                <div className="form-group"><label>Data</label><input type="date" value={formCusto.data} onChange={e => setFormCusto(p => ({ ...p, data:e.target.value }))} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCustoModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarCustoManual}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL CADASTRO/EDIÇÃO ═══ */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editando ? 'Editar Animal' : 'Novo Animal'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Brinco *</label><input value={form.brinco} onChange={e => f('brinco', e.target.value)} placeholder="Ex: OV-001" /></div>
                <div className="form-group"><label>Nome / Apelido</label><input value={form.nome} onChange={e => f('nome', e.target.value)} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Sexo *</label>
                  <select value={form.sexo} onChange={e => f('sexo', e.target.value)}>
                    <option value="M">Macho</option><option value="F">Fêmea</option>
                  </select>
                </div>
                <div className="form-group"><label>Data de Nascimento</label>
                  <input type="date" value={form.data_nascimento} onChange={e => f('data_nascimento', e.target.value)} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Raça</label>
                  <select value={form.raca_id} onChange={e => f('raca_id', e.target.value)}>
                    <option value="">Selecione</option>
                    {racasDisponiveis.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Lote</label>
                  <select value={form.lote_id} onChange={e => f('lote_id', e.target.value)}>
                    <option value="">Selecione</option>
                    {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Categoria {form.data_nascimento && form.sexo && <span style={{ color:'var(--color-primary)', fontSize:12 }}>(calculada automaticamente)</span>}</label>
                  <select value={form.categoria} onChange={e => f('categoria', e.target.value)}>
                    <option value="">Auto (pela idade)</option>
                    {CATEGORIAS.map(c => <option key={c} value={c} style={{ textTransform:'capitalize' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Situação</label>
                  <select value={form.situacao} onChange={e => f('situacao', e.target.value)}>
                    {['ativo','vendido','descarte','obito','quarentena'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Origem</label>
                <select value={form.origem} onChange={e => f('origem', e.target.value)}>
                  <option value="nascido">Nascido na propriedade</option>
                  <option value="comprado">Comprado</option>
                  <option value="doado">Doado</option>
                </select>
              </div>
              {form.origem === 'comprado' && (
                <div className="form-row">
                  <div className="form-group"><label>Data de entrada</label><input type="date" value={form.data_entrada} onChange={e => f('data_entrada', e.target.value)} /></div>
                  <div className="form-group"><label>Valor de aquisição (R$)</label><input type="number" min="0" step="0.01" value={form.valor_compra} onChange={e => f('valor_compra', e.target.value)} /></div>
                </div>
              )}
              <div className="form-group"><label>Observações</label><textarea rows={3} value={form.observacoes} onChange={e => f('observacoes', e.target.value)} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.brinco}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
